/**
 * Telemetry-instrumented toast — the only way the app raises a toast.
 *
 * `sonner` is imported here and in `components/ui/crucible-toaster.tsx` and
 * nowhere else (ESLint `no-restricted-imports` + tests/lib/notification-surfaces
 * enforce it). Every surfaced toast emits a `notification_shown` customEvent in
 * AppInsights via trackNotification(), tagged with surface='toast' and the
 * appropriate kind, so a single KQL slicer covers the whole user-facing
 * attention pipeline.
 *
 * What the wrapper adds on top of sonner:
 *   - telemetry, once per appearance (re-using an `id` to update a toast in
 *     place does not re-count it);
 *   - default durations by severity (see TOAST_DURATION_MS) — a toast that
 *     carries an `action`, or sets `duration: Infinity`, stays until handled;
 *   - `loading` and `promise` built on typed toasts, so they carry a × too;
 *   - re-raising an `id` without an `icon` drops the icon the toast had, so a
 *     spinner does not survive into the finished success / error toast.
 *
 * Dismissal is the Toaster's own × (CrucibleToaster sets `closeButton`), so the
 * wrapper no longer injects a "Dismiss" cancel button. Callers may still pass
 * an explicit `cancel` ("Later").
 *
 * Every toast it raises has that ×. Sonner draws none on its own `loading`
 * type or on `custom` (JSX) toasts, so:
 *   - `toast.loading` is a persistent `info` toast with a spinner icon;
 *   - `toast.promise` is that loading toast, updated in place (same id) to a
 *     success or error toast when the promise settles;
 *   - `toast.custom` is not offered at all. A whole-JSX toast cannot carry the
 *     Toaster's ×; pass a typed variant a `description` / `icon` instead.
 *
 * AB#19124, slice 2 (toast wrapper) + slice 4 (unified notification model).
 */

import { createElement, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { toast as _toast } from 'sonner'
import type { ExternalToast } from 'sonner'
import { trackNotification, type NotificationKind } from './telemetry'

// A title: text, a node, or (as sonner allows) a function returning one.
type ToastMessage = Parameters<typeof _toast.loading>[0]
type ToastId = string | number
// Sonner exposes 'success' | 'error' | 'info' | 'warning' | 'message' as
// distinct variants. We collapse 'message' (bare toast()) onto 'info' for
// the unified NotificationKind, since both are neutral non-actionable.
// 'loading' and 'promise' are not severities; they are reported as neutral
// info too.
type ToastVariant =
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'message'
  | 'loading'
  | 'promise'

/** Sonner's per-toast options, re-exported so no other module imports `sonner`. */
export type ToastOptions = ExternalToast

/** The variants that render as a typed (icon-coloured) toast. */
export type TypedToastVariant = 'success' | 'error' | 'info' | 'warning'

const VARIANT_TO_KIND: Record<ToastVariant, NotificationKind> = {
  success: 'success',
  error: 'error',
  info: 'info',
  warning: 'warning',
  message: 'info',
  loading: 'info',
  promise: 'info',
}

/**
 * How long each severity stays on screen when the caller does not say.
 * Errors linger longest because they are the ones people need to read and act
 * on; a confirmation is gone before it becomes noise. `loading` (and a
 * pending `promise`) is absent on purpose: it stays until updated or closed.
 */
export const TOAST_DURATION_MS = {
  success: 4_000,
  info: 4_000,
  message: 4_000,
  warning: 6_000,
  error: 10_000,
} as const

/** Per-surface telemetry for toasts that are a named notification surface. */
export interface ToastTelemetry {
  surface: string
  kind: NotificationKind
  props?: Record<string, string>
}

/**
 * Render a toast message to a short string for telemetry. Most callers pass
 * plain strings; React-node toasts (custom JSX) get a generic placeholder
 * since stringifying ReactNode is unreliable and noisy.
 */
function summarize(message: unknown): string {
  if (typeof message === 'string') return message.slice(0, 200)
  if (typeof message === 'number' || typeof message === 'boolean') return String(message)
  return '<jsx>'
}

/**
 * Toasts currently on screen that were given an explicit `id`, with the variant
 * they were last raised as. A caller that re-raises the same id to update it
 * (a countdown, a progress line) is not a new appearance and is not re-counted;
 * a change of variant (running → done) is.
 */
const liveIds = new Map<ToastId, ToastVariant>()

function track(
  variant: ToastVariant,
  message: unknown,
  data?: ExternalToast,
  telemetry?: ToastTelemetry,
) {
  const id = data?.id
  if (id !== undefined) {
    if (liveIds.get(id) === variant) return
    liveIds.set(id, variant)
  }
  if (telemetry) {
    trackNotification(telemetry.surface, telemetry.kind, telemetry.props)
    return
  }
  trackNotification('toast', VARIANT_TO_KIND[variant], {
    variant,
    summary: summarize(message),
    hasDescription: data?.description ? 'true' : 'false',
  })
}

/** Forget an id once its toast is gone, so a later toast with it counts again. */
function forget(id: ToastId | undefined) {
  if (id === undefined) liveIds.clear()
  else liveIds.delete(id)
}

/**
 * The duration a toast gets when the caller did not set one: an explicit value
 * always wins; an `action` means the user has something to do, so it stays;
 * otherwise severity decides.
 */
function resolveDuration(variant: ToastVariant, data?: ExternalToast): number | undefined {
  if (data?.duration !== undefined) return data.duration
  if (data?.action) return Infinity
  if (variant === 'success' || variant === 'info' || variant === 'message') {
    return TOAST_DURATION_MS[variant]
  }
  if (variant === 'warning' || variant === 'error') return TOAST_DURATION_MS[variant]
  return undefined
}

/**
 * Apply the default duration and keep the telemetry bookkeeping honest: when a
 * toast with an `id` goes away (swipe, ×, timeout, a button), forget the id.
 * Callers' own callbacks still run.
 */
function normalizeOptions<T extends ExternalToast>(variant: ToastVariant, data?: T): T | undefined {
  const duration = resolveDuration(variant, data)
  if (!data && duration === undefined) return data

  const id = data?.id
  // `closeButton: true` last, so a caller cannot switch off the × that every
  // toast must carry; the Toaster's own default covers a call with no options.
  const next: T = { ...data, ...(duration === undefined ? {} : { duration }), closeButton: true } as T
  if (id === undefined) return next

  // Sonner merges an update into the toast it replaces, icon included. Without
  // this a spinner raised by `loading` (or a running job) would stay on the
  // success / error toast that replaces it. An explicit `icon` still wins.
  if (!('icon' in next)) next.icon = undefined

  const clear = () => forget(id)
  next.onDismiss = (t) => {
    clear()
    data?.onDismiss?.(t)
  }
  next.onAutoClose = (t) => {
    clear()
    data?.onAutoClose?.(t)
  }
  return next
}

function emit(
  variant: ToastVariant,
  message: ToastMessage,
  data: ExternalToast | undefined,
  telemetry: ToastTelemetry | undefined,
  fire: (message: ToastMessage, opts?: ExternalToast) => ToastId,
): ToastId {
  const opts = normalizeOptions(variant, data)
  track(variant, message, opts, telemetry)
  return fire(message, opts)
}

const bareToast = (message: ToastMessage, data?: ExternalToast) =>
  emit('message', message, data, undefined, (m, o) => _toast(m, o))

/**
 * Raise a typed toast that is also a named notification surface (session
 * guard, version guard, …). Telemetry is reported under `telemetry.surface`
 * and `kind` instead of the generic `toast` surface, so those events keep the
 * names the dashboards already slice on. Same durations and `id` update-in-place
 * behaviour as the plain helpers.
 */
export function notifyToast(
  variant: TypedToastVariant,
  message: ToastMessage,
  data: ExternalToast | undefined,
  telemetry: ToastTelemetry,
): ToastId {
  return emit(variant, message, data, telemetry, (m, o) => _toast[variant](m, o))
}

/** The spinner a `loading` toast shows in place of a severity icon. */
function spinnerIcon(): ReactNode {
  return createElement(Loader2, {
    className: 'size-4 animate-spin motion-reduce:animate-none',
    'aria-hidden': true,
  })
}

function raiseLoading(variant: 'loading' | 'promise', message: ToastMessage, data?: ExternalToast): ToastId {
  return emit(variant, message, { icon: spinnerIcon(), duration: Infinity, ...data }, undefined, (m, o) =>
    _toast.info(m, o),
  )
}

/**
 * A toast for work in progress: a spinner, no auto-close, and the Toaster's ×.
 * Re-raise its `id` as `success` / `error` (or `dismiss` it) when the work ends.
 */
const loading = (message: ToastMessage, data?: ExternalToast): ToastId => raiseLoading('loading', message, data)

/** Text for a settled promise: fixed, or worked out from its value / error. */
type Settled<V> = ToastMessage | ((value: V) => ToastMessage)

export interface PromiseToastMessages<T> {
  loading: ToastMessage
  success: Settled<T>
  error: Settled<unknown>
  /** Reuse an existing toast; otherwise one is made up. */
  id?: ToastId
  description?: ReactNode
}

let promiseSeq = 0

/**
 * A loading toast that turns into a success or error toast, in place, when
 * `work` settles. Returns the toast's id. A rejection is reported on the toast
 * and not re-thrown here; keep your own handle on `work` to react to it.
 */
function promise<T>(work: Promise<T> | (() => Promise<T>), messages: PromiseToastMessages<T>): ToastId {
  promiseSeq += 1
  const id = messages.id ?? `toast-promise-${promiseSeq}`
  raiseLoading('promise', messages.loading, {
    id,
    ...(messages.description === undefined ? {} : { description: messages.description }),
  })
  const settle = <V>(text: Settled<V>, value: V): ToastMessage =>
    typeof text === 'function' ? (text as (v: V) => ToastMessage)(value) : text
  // A factory that throws before returning a promise is reported like a
  // rejection, so the loading toast never outlives it.
  let pending: Promise<T>
  try {
    pending = typeof work === 'function' ? work() : work
  } catch (error) {
    pending = Promise.reject(error)
  }
  pending.then(
    (value) => {
      toast.success(settle(messages.success, value), { id })
    },
    (error: unknown) => {
      toast.error(settle(messages.error, error), { id })
    },
  )
  return id
}

const dismiss: typeof _toast.dismiss = (id) => {
  forget(id)
  return _toast.dismiss(id)
}

function typed(variant: TypedToastVariant | 'message') {
  return (message: ToastMessage, data?: ExternalToast) =>
    emit(variant, message, data, undefined, (m, o) => _toast[variant](m, o))
}

/**
 * The app's toast API: Sonner's, minus `custom` (no × possible), with
 * `loading` and `promise` replaced by the dismissible versions above.
 */
export interface CrucibleToast {
  (message: ToastMessage, data?: ExternalToast): ToastId
  success: (message: ToastMessage, data?: ExternalToast) => ToastId
  error: (message: ToastMessage, data?: ExternalToast) => ToastId
  info: (message: ToastMessage, data?: ExternalToast) => ToastId
  warning: (message: ToastMessage, data?: ExternalToast) => ToastId
  message: (message: ToastMessage, data?: ExternalToast) => ToastId
  loading: (message: ToastMessage, data?: ExternalToast) => ToastId
  promise: typeof promise
  dismiss: typeof _toast.dismiss
}

export const toast: CrucibleToast = Object.assign(bareToast, {
  success: typed('success'),
  error: typed('error'),
  info: typed('info'),
  warning: typed('warning'),
  message: typed('message'),
  loading,
  promise,
  dismiss,
})
